import { eq } from "@-/com.npmjs.drizzle-orm";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import {
  customLevelPacksSchema,
  saveCustomLevelPack,
  type CustomLevelPack,
} from "@-/dev.improve.haniverse.lib.level.custom";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";

export function useSaveCustomLevelPack() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const userOptions = useActorUserDataQueryOptions();
  return useMutation({
    mutationFn: async (
      { original, pack }: { original: CustomLevelPack | null; pack: CustomLevelPack },
      { client },
    ) => {
      if (!pack.name.trim() || pack.levels.some((level) => !level.name.trim())) {
        throw new Error("Name the pack and every level before saving.");
      }
      const { user: identity } = await withDeadline(
        { deadline: 30_000, operation: "resolve custom pack owner" },
        () => client.fetchQuery(userOptions),
      );
      return withDeadline({ deadline: 30_000, operation: "save custom level pack" }, () =>
        actorDb(import.meta.location).transaction(async (tx) => {
          const user = await tx.query.user.findFirst({ where: { id: identity.id } });
          if (user === undefined) {
            throw new Error("The pack owner could not be found. Try again.");
          }
          const document = customLevelPacksSchema.parse(user.customLevelPacks);
          const customLevelPacks = saveCustomLevelPack(document, pack, original);
          await tx
            .update(actorSchema.user)
            .set({ customLevelPacks })
            .where(eq(actorSchema.user.id, user.id))
            .run();
          return { ...user, customLevelPacks };
        }),
      );
    },
    mutationKey: ["saveCustomLevelPack"],
  });
}
